import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog } from "@mui/material";
import { Close, RotateRight, ZoomIn } from "@mui/icons-material";
import { toast } from "react-toastify";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // Needed when imageSrc is a remote cross-origin URL (e.g. adjusting an
    // already-uploaded photo's crop) — without this, drawImage/toBlob below
    // silently taints the canvas and toBlob fails. Harmless for blob:/data:
    // URLs (a freshly-selected local file), which ignore crossOrigin anyway.
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    // Cache-bust remote URLs: the same URL was likely already displayed on
    // the page via a plain <img>/CSS background (no CORS mode), and a cached
    // opaque response for it can't be reused for this CORS-mode load.
    img.src = src.startsWith("http")
      ? `${src}${src.includes("?") ? "&" : "?"}cb=${Date.now()}`
      : src;
  });

const getRadianAngle = (degrees) => (degrees * Math.PI) / 180;

// The bounding box that fully contains the image once rotated.
const rotatedBoundingBox = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const cropToBlob = async (imageSrc, area, rotation = 0, mimeType = "image/jpeg") => {
  const image = await loadImage(imageSrc);
  const rotRad = getRadianAngle(rotation);
  const { width: boxWidth, height: boxHeight } = rotatedBoundingBox(
    image.width,
    image.height,
    rotation,
  );

  // react-easy-crop reports croppedAreaPixels relative to the rotated
  // bounding box (not the original unrotated image) whenever rotation != 0,
  // so the full image must be rotated onto its own canvas first, then
  // cropped from that.
  const rotatedCanvas = document.createElement("canvas");
  rotatedCanvas.width = boxWidth;
  rotatedCanvas.height = boxHeight;
  const rotatedCtx = rotatedCanvas.getContext("2d");
  rotatedCtx.translate(boxWidth / 2, boxHeight / 2);
  rotatedCtx.rotate(rotRad);
  rotatedCtx.translate(-image.width / 2, -image.height / 2);
  rotatedCtx.drawImage(image, 0, 0);

  const canvas = document.createElement("canvas");
  canvas.width = area.width;
  canvas.height = area.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    rotatedCanvas,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    area.width,
    area.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType, 0.9));
};

// A reusable "crop before upload" step — pass the raw selected file's
// object URL in, get a cropped Blob back via onCropped.
export const ImageCropModal = ({ open, imageSrc, aspect = 1, onCancel, onCropped }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset transient crop state whenever a new image is loaded into the
  // modal, so reopening it for a different photo doesn't carry over the
  // previous zoom/rotation/position.
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const handleCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await cropToBlob(imageSrc, croppedAreaPixels, rotation);
      if (!blob) throw new Error("Crop produced no image data");
      await onCropped(blob);
    } catch (err) {
      console.error(err);
      toast("Failed to crop image", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#000C66] font-aref text-base">Crop Photo</h3>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition"
          >
            <Close fontSize="small" />
          </button>
        </div>

        <div className="relative w-full h-72 bg-gray-900 rounded-xl overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <ZoomIn style={{ color: "#9ca3af", fontSize: 18 }} />
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
        </div>

        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => setRotation((r) => (r + 90) % 360)}
            title="Rotate 90°"
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 transition flex-shrink-0"
          >
            <RotateRight style={{ fontSize: 18 }} />
          </button>
          <input
            type="range"
            min={0}
            max={359}
            step={1}
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            className="flex-1"
          />
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-abel font-medium hover:bg-gray-50 transition text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !croppedAreaPixels}
            className="flex-1 py-2.5 rounded-xl bg-[#000C66] text-white font-abel font-semibold hover:bg-blue-900 transition text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving && (
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            Use Photo
          </button>
        </div>
      </div>
    </Dialog>
  );
};
